import React from "react";
import { injectIntl } from "react-intl";
import PropTypes from "prop-types";
import { getIn } from "final-form";
import { FormSpy } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { localize } from "cci-utility";
import { Container } from "../layout/common";
import Error from "../field/Error";
import is from "ramda/src/is";

/**
 * Form field that contains an array with various fields in itself as its children.
 * This is used to handle to-many relationships and allows the user to update and add records to the relationship.
 * 
 * In order to allow the user to add a new record, an empty child component is added to the end of the array.
 * 
 * Props:
 * - fieldWrapper: Function that can be used to wrap the Container within the Field. This is useful to implement drag and drop. This function is called with the move and children properties.
 * - name: Name of the to-many relationship that contains the records.
 * - addValidation: A yup validation schema used to determine if the fields in the empty child component should be added as a record.
 * - onAdd: Function that is called when a new item is added to the array. This can be used to recalulate the sequence for example.
 * - children: A function that receives various arguments:
 *   name: Composed name of the field which can be used to add sub-fields in this to-many field.
 *   isNew: Indicates if the child component being rendered is the empty one for the user to fille out to create a new record.
 *   remove: Function that can be called to remove the current record.
 *   push: Function that can be called to determine if a new record can be created and added to the records array.
 *   value: Value of the To Many Field (array of objects).
 */
class FormToManyField extends React.PureComponent {
  constructor() {
    super();
    this.state = { plusButtonPressed: false };
  }

  render() {
    const {
        intl,
        fieldWrapper,
        name,
        addValidation,
        onAdd,
        children,
        renderNewOnButton,
        renderNewOnEmpty,
        defaultValues,
        ...restProps
      } = this.props,
      { plusButtonPressed } = this.state;
    /*
     * We dynamically create a new object in the form state, to store all of the information that the user has typed into
     * the "new" child component. We use this to be able to validate that the data is complete AND to be able to easily add it
     * to the array. The "__new" object should be removed prior to sending any request to the server (with a link if using Apollo).
     */
    const newKey = `__new.${name.replace(/\.|\[|\]/gi, "_")}`;
    return (
      <FieldArray name={name}>
        {({ fields: { map, push, remove, move }, meta }) => (
          // We want the FormSpy to be rendered only when FieldArray children is rendered, so we pass an empty object as subscription prop
          <FormSpy subscription={{}}>
            {({ form }) => {
              const error =
                !is(Array, meta.error || meta.submitError) &&
                localize(intl, meta.error || meta.submitError),
                length = form.getFieldState(name) && form.getFieldState(name).value && form.getFieldState(name).value.length;
              let value = getIn(form.getState().values, name) || [];
              value = value.map(item => ({ ...defaultValues, ...item }));

              return fieldWrapper({
                form,
                move,
                name,
                children: (
                  // Outer container is here only to contain the error message
                  <React.Fragment>
                    {/* Contains the records */}
                    <Container
                      flexDirection="row"
                      flexWrap="wrap"
                      {...restProps}
                    >
                      {/* Render the records currently in the relationship */}
                      {map((name, index) =>
                        children({
                          name,
                          index,
                          remove: () => remove(index),
                          value,
                          plus: () => {
                            this.setState({ plusButtonPressed: true });
                          },
                          length
                        })
                      )}

                      {/* Empty version of the child component which allows the user to enter information for a new record */}
                      {((renderNewOnButton && plusButtonPressed) ||
                        !renderNewOnButton ||
                        (renderNewOnButton && renderNewOnEmpty &&
                          form.getFieldState(name) &&
                          !form.getFieldState(name).value) ||
                        (renderNewOnButton && renderNewOnEmpty &&
                          form.getFieldState(name) &&
                          form.getFieldState(name).value.length === 0)) &&
                        children({
                          name: newKey,
                          isNew: true,
                          value,
                          push: () => {
                            // Here we obtain the data for just the "new" child component
                            let data = getIn(form.getState().values, newKey);
                            data = { ...defaultValues, ...data };

                            // Either no yup validation schema was added OR the validation schema is valid
                            const isValid =
                              !addValidation || addValidation.isValidSync(data);

                            if (isValid === true) {
                              // Add the data from the "new" child component to the end of the array
                              push(data);

                              // Hiding the new item again
                              this.setState({ plusButtonPressed: false });

                              // Call event that an item was added to the array (used to recalculate sequence)
                              onAdd({ form, name });

                              // Reset the "new" child component so that the user can add a new record if needed
                              form.change(newKey, {});
                            }
                          }
                        })}
                    </Container>

                    {/* Error Message */}
                    {error && <Error>{error}</Error>}
                  </React.Fragment>
                )
              });
            }}
          </FormSpy>
        )}
      </FieldArray>
    );
  }
}

FormToManyField.propTypes = {
  name: PropTypes.string.isRequired,
  fieldWrapper: PropTypes.func.isRequired,
  renderNewOnEmpty: PropTypes.bool.isRequired
};

FormToManyField.defaultProps = {
  fieldWrapper: ({ children }) => children,
  onAdd: () => {},
  renderNewOnEmpty: true
};

export default injectIntl(FormToManyField);
